import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

export default function DataTable({
  columns = [],
  rows = [],
  page: externalPage,
  rowsPerPage: externalRowsPerPage,
  count: externalCount,
  onPageChange,
  onRowsPerPageChange,
  showPagination = true,
  loading = false,
  noDataText = "No data to display",
  onRowClick,
}) {
  // Estado interno para paginación automática
  const [internalPage, setInternalPage] = React.useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = React.useState(5);

  // Determinar si usar paginación externa o interna
  // Si se proporcionan las funciones de callback, usar paginación externa
  const useExternalPagination = onPageChange && onRowsPerPageChange;

  const page = useExternalPagination ? externalPage ?? 0 : internalPage;
  const rowsPerPage = useExternalPagination
    ? externalRowsPerPage ?? 5
    : internalRowsPerPage;
  const count = useExternalPagination
    ? externalCount ?? rows.length
    : rows.length;

  const handlePageChange = (event, newPage) => {
    if (useExternalPagination) {
      onPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = +event.target.value;
    if (useExternalPagination) {
      onRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalPage(0);
    }
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="tabla con paginado">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={120}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={120}
                  >
                    {noDataText}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (useExternalPagination
                ? rows
                : rows.slice(
                    showPagination ? page * rowsPerPage : 0,
                    showPagination
                      ? page * rowsPerPage + rowsPerPage
                      : rows.length
                  )
              ).map((row, rowIndex) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row.id || rowIndex}
                  onClick={
                    onRowClick ? () => onRowClick(row) : undefined
                  }
                  sx={
                    onRowClick
                      ? { cursor: "pointer" }
                      : undefined
                  }
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    if (column.id === "action" && column.actions) {
                      return (
                        <TableCell key={column.id} align={column.align}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {column.actions.map((action, idx) =>
                              typeof action === "function" ? (
                                action(row)
                              ) : (
                                <IconButton
                                  key={idx}
                                  title={action.label}
                                  onClick={() => action.onClick(row)}
                                >
                                  {action.icon}
                                </IconButton>
                              )
                            )}
                          </div>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format ? column.format(value, row) : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage="Rows per page"
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </Paper>
  );
}
